import { useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import CityScene from './scene/CityScene';
import NowPlayingBar from './ui/NowPlayingBar';
import LoadingScreen from './ui/LoadingScreen';
import AudioUnlocker from './ui/AudioUnlocker';
import Minimap from './ui/Minimap';
import TrackModal from './ui/TrackModal';
import VirtualJoystick from './ui/VirtualJoystick';
import LandingPage from './ui/LandingPage';
import { loadCityData } from './data/loadCity';
import { audioManager } from './audio/AudioManager';
import { useGameStore } from './store/gameStore';
import type { CityData } from './types/city';

/**
 * Preload all album art images so they're cached before Three.js tries to load them.
 * Each Stage component creates its own Image(), but if the browser has already
 * cached the URL, the load is instant and never races with StrictMode double-mounts.
 */
async function preloadArtwork(data: CityData): Promise<void> {
  const urls = data.districts.flatMap((d) => d.tracks.map((t) => t.albumArt));
  const unique = [...new Set(urls)];

  await Promise.all(
    unique.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => {
            console.warn(`Artwork preload failed: ${url}`);
            resolve(); // don't block — the Stage retry will handle it
          };
          img.src = url;
          // If already cached, onload may have fired synchronously
          if (img.complete) resolve();
        }),
    ),
  );
}

export default function App() {
  const [cityData, setCityData] = useState<CityData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preloaded, setPreloaded] = useState(false);
  const [showGame, setShowGame] = useState(false);
  const loading = useGameStore((s) => s.loading);
  const setLoading = useGameStore((s) => s.setLoading);

  useEffect(() => {
    loadCityData()
      .then(async (data) => {
        setCityData(data);
        useGameStore.getState().loadCity(data);

        // Preload all album art images before game starts
        // (prevents intermittent white textures from race conditions)
        await preloadArtwork(data);

        // Init and preload all audio players upfront
        await audioManager.init(data);
        await audioManager.preloadAll();

        setPreloaded(true);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load city:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [setLoading]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-[#0d0d1a] to-[#1a1025] text-white">
        <div className="text-center">
          <p className="text-red-400 text-xl mb-2">Failed to load city</p>
          <p className="text-white/50 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Show landing page until user clicks DISCOVER
  if (!showGame) {
    const trackCount = cityData
      ? cityData.districts.reduce((sum, d) => sum + d.tracks.length, 0)
      : undefined;

    return (
      <LandingPage
        onEnter={() => setShowGame(true)}
        preloaded={preloaded}
        trackCount={trackCount}
      />
    );
  }

  // Still loading city data after clicking enter? Show loader briefly
  if (loading || !cityData || !preloaded) {
    return <LoadingScreen />;
  }

  return (
    <div className="w-full h-full relative">
      {/* Click-to-enable-audio overlay (browser autoplay policy) */}
      <AudioUnlocker />

      <Canvas
        shadows
        gl={{ antialias: true, alpha: false }}
      >
        {/* Atmospheric fog — depth cue for distant stages */}
        <fog attach="fog" args={['#996644', 20, 60]} />
        <CityScene cityData={cityData} />
      </Canvas>

      {/* HUD overlay */}
      <NowPlayingBar />

      {/* Controls hint */}
      <div className="fixed top-4 left-4 pointer-events-none">
        <div className="bg-black/40 backdrop-blur-sm rounded-lg px-3 py-1.5 text-white/70 text-xs">
          A/D to turn &middot; W/S to walk
        </div>
      </div>

      {/* Minimap */}
      <Minimap cityData={cityData} />

      {/* Track info modal */}
      <TrackModal />

      {/* Mobile virtual joystick */}
      <VirtualJoystick />
    </div>
  );
}
